import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarCheck2, CheckCircle2, ChevronLeft, ChevronRight, GraduationCap, Loader2, Plus, RefreshCw, UserRound, Users, X } from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';

import { api } from '@/services/api';
import { ProtectedComponent } from '@/components/common/ProtectedComponent';
import Modal from '@/components/ui/Modal';
import { Permission } from '@/types/permissions';
import { usePermissions } from '@/hooks/usePermissions';
import { getContentApiUrl } from '@/config/api.config';
import ContentManagement from './ContentManagement';
import ProgrammeCalendar from '@/components/scheduling/ProgrammeCalendar';
import {
  ProgrammeCapacityFlag,
  ProgrammeCalendarResponse,
  SchedulingOverview,
  Programme,
  ProgrammeEnrollment,
  ProgrammeSession,
  PrivateLesson,
  PrivateLessonParticipant,
  Location,
  Court,
} from '@/types';

const STATUS_COLOR: Record<string, string> = {
  draft: 'badge-gray',
  open: 'badge-primary',
  full: 'badge-warning',
  in_progress: 'badge-success',
  completed: 'badge-success',
  cancelled: 'badge-danger',
  pending: 'badge-warning',
  confirmed: 'badge-primary',
  active: 'badge-success',
  waitlisted: 'badge-warning',
  withdrawn: 'badge-gray',
};

const PROGRAMME_STATUS_OPTIONS: Programme['status'][] = ['draft', 'open', 'full', 'in_progress', 'completed', 'cancelled'];
const PRIVATE_STATUS_OPTIONS: PrivateLesson['status'][] = ['pending', 'confirmed', 'completed', 'cancelled'];
type ProgrammeStatusFilter = 'active' | 'all' | Programme['status'];
const DEFAULT_PROGRAMME_STATUS_FILTER: ProgrammeStatusFilter = 'active';

const defaultOverview: SchedulingOverview = {
  summary: {
    programmes_total: 0,
    programmes_open: 0,
    active_enrollments: 0,
    private_lessons_today: 0,
    private_lessons_pending: 0,
  },
  recent_programmes: [],
  generated_at: new Date().toISOString(),
};

type ProgrammeCenterView = 'programme' | 'private' | 'profiles' | 'promotion';
type ProgrammeDisplayMode = 'list' | 'calendar';
type AssistantUiEffectHighlightKey = 'programme_id' | 'session_id' | 'enrollment_id' | 'private_lesson_id';

type ContentProgrammeProfile = {
  id: string;
  title: string;
};

type ProgrammeFormState = {
  id?: number;
  title: string;
  description: string;
  level: Programme['level'];
  location_id: string;
  coach_name: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  min_participants: number;
  max_participants: number;
  content_programme_id: string;
  status: Programme['status'];
  notes: string;
};

const makeDefaultProgrammeForm = (): ProgrammeFormState => {
  const today = new Date().toISOString().slice(0, 10);
  return {
    title: '',
    description: '',
    level: 'mixed',
    location_id: '',
    coach_name: '',
    start_date: today,
    end_date: today,
    start_time: '18:00',
    end_time: '19:00',
    min_participants: 4,
    max_participants: 12,
    content_programme_id: '',
    status: 'draft',
    notes: '',
  };
};

type PrivateLessonFormState = {
  id?: number;
  title: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  participant_limit: number;
  date: string;
  start_time: string;
  end_time: string;
  is_flexible_time: boolean;
  location_id: string;
  court_id: string;
  coach_name: string;
  status: PrivateLesson['status'];
  notes: string;
};

const makeDefaultPrivateLessonForm = (): PrivateLessonFormState => ({
  title: 'Private Coaching',
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  participant_limit: 4,
  date: new Date().toISOString().slice(0, 10),
  start_time: '18:00',
  end_time: '19:00',
  is_flexible_time: false,
  location_id: '',
  court_id: '',
  coach_name: '',
  status: 'pending',
  notes: '',
});

const getCurrentWeekStart = (): string => {
  const today = new Date();
  const day = today.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  today.setDate(today.getDate() + mondayOffset);
  return today.toISOString().slice(0, 10);
};

const shiftDate = (dateText: string, days: number): string => {
  const d = new Date(`${dateText}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const isProgrammeEnrollmentBlocked = (status?: Programme['status'] | null) => {
  const normalized = String(status || '').toLowerCase();
  return normalized === 'cancelled' || normalized === 'completed';
};

const CourseScheduler: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const canManageProgrammes = hasPermission(Permission.MANAGE_PROGRAMMES);
  const canManagePrivateLessons = hasPermission(Permission.MANAGE_PRIVATE_LESSONS);
  const canManageContent = hasPermission(Permission.MANAGE_CONTENT);

  const availableViews = useMemo<ProgrammeCenterView[]>(() => {
    const views: ProgrammeCenterView[] = [];
    if (canManageProgrammes) views.push('programme');
    if (canManagePrivateLessons) views.push('private');
    if (canManageContent) views.push('profiles', 'promotion');
    return views.length > 0 ? views : ['programme'];
  }, [canManageContent, canManagePrivateLessons, canManageProgrammes]);

  const [activeTab, setActiveTab] = useState<ProgrammeCenterView>('programme');
  const [loading, setLoading] = useState(false);

  const [overview, setOverview] = useState<SchedulingOverview>(defaultOverview);
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [privateLessons, setPrivateLessons] = useState<PrivateLesson[]>([]);

  const [locations, setLocations] = useState<Location[]>([]);
  const [courts, setCourts] = useState<Court[]>([]);
  const [contentProgrammeProfiles, setContentProgrammeProfiles] = useState<ContentProgrammeProfile[]>([]);

  const [programmeSearch, setProgrammeSearch] = useState('');
  const [programmeStatus, setProgrammeStatus] = useState<ProgrammeStatusFilter>(DEFAULT_PROGRAMME_STATUS_FILTER);
  const [programmeDateFrom, setProgrammeDateFrom] = useState('');
  const [programmeDateTo, setProgrammeDateTo] = useState('');
  const [programmeCoach, setProgrammeCoach] = useState('');
  const [programmeCapacityFlag, setProgrammeCapacityFlag] = useState<'all' | ProgrammeCapacityFlag>('all');
  const [programmeDisplayMode, setProgrammeDisplayMode] = useState<ProgrammeDisplayMode>('list');
  const [programmeCalendar, setProgrammeCalendar] = useState<ProgrammeCalendarResponse>({
    range: {
      start: getCurrentWeekStart(),
      end: shiftDate(getCurrentWeekStart(), 6),
    },
    filters: {},
    items: [],
  });
  const [programmeCalendarLoading, setProgrammeCalendarLoading] = useState(false);
  const [privateSearch, setPrivateSearch] = useState('');
  const [privateStatus, setPrivateStatus] = useState<string>('all');
  const programmeTableRef = useRef<HTMLDivElement | null>(null);
  const [showProgrammeScrollHint, setShowProgrammeScrollHint] = useState(false);
  const [highlightToken, setHighlightToken] = useState<{
    key: AssistantUiEffectHighlightKey;
    value: number;
    expiresAt: number;
  } | null>(null);
  const highlightTimerRef = useRef<number | null>(null);

  const [programmeModalOpen, setProgrammeModalOpen] = useState(false);
  const [programmeForm, setProgrammeForm] = useState<ProgrammeFormState>(makeDefaultProgrammeForm());
  const [savingProgramme, setSavingProgramme] = useState(false);

  const [privateModalOpen, setPrivateModalOpen] = useState(false);
  const [privateForm, setPrivateForm] = useState<PrivateLessonFormState>(makeDefaultPrivateLessonForm());
  const [savingPrivateLesson, setSavingPrivateLesson] = useState(false);

  const [selectedProgramme, setSelectedProgramme] = useState<Programme | null>(null);
  const [programmeDetail, setProgrammeDetail] = useState<{
    sessions: ProgrammeSession[];
    enrollments: ProgrammeEnrollment[];
  }>({ sessions: [], enrollments: [] });
  const [programmeDetailOpen, setProgrammeDetailOpen] = useState(false);
  const [sessionForm, setSessionForm] = useState({
    session_date: new Date().toISOString().slice(0, 10),
    start_time: '18:00',
    end_time: '19:00',
    court_id: '',
    capacity: '',
    notes: '',
  });
  const [enrollmentForm, setEnrollmentForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    status: 'active' as 'active' | 'waitlisted',
    auto_waitlist: true,
  });
  const [savingEnrollment, setSavingEnrollment] = useState(false);

  const [selectedPrivateLesson, setSelectedPrivateLesson] = useState<PrivateLesson | null>(null);
  const [privateLessonDetail, setPrivateLessonDetail] = useState<{
    participants: PrivateLessonParticipant[];
  }>({ participants: [] });
  const [privateDetailOpen, setPrivateDetailOpen] = useState(false);
  const [participantForm, setParticipantForm] = useState({
    participant_name: '',
    participant_email: '',
    participant_phone: '',
  });

  const filteredProgrammes = useMemo(() => {
    const matchCapacityFlag = (item: Programme) => {
      if (programmeCapacityFlag === 'all') return true;
      const enrolled = Number(item.enrolled_count || 0);
      const min = Number(item.min_participants || 0);
      const max = Number(item.max_participants || 0);
      if (programmeCapacityFlag === 'under_min') return enrolled < min;
      if (programmeCapacityFlag === 'full') return enrolled >= max;
      if (programmeCapacityFlag === 'near_full') {
        return max > 0 && enrolled * 10 >= max * 8 && enrolled < max;
      }
      return true;
    };

    const searchText = programmeSearch.trim().toLowerCase();
    const coachText = programmeCoach.trim().toLowerCase();
    return programmes.filter((item) => {
      const matchSearch = !searchText
        || item.title.toLowerCase().includes(searchText)
        || item.code.toLowerCase().includes(searchText)
        || (item.coach_name || '').toLowerCase().includes(searchText);
      const matchStatus = programmeStatus === 'all'
        || (programmeStatus === 'active' ? item.status !== 'cancelled' : item.status === programmeStatus);
      const matchCoach = !coachText || (item.coach_name || '').toLowerCase().includes(coachText);
      const matchDateFrom = !programmeDateFrom || item.start_date >= programmeDateFrom;
      const matchDateTo = !programmeDateTo || item.end_date <= programmeDateTo;
      return matchSearch && matchStatus && matchCoach && matchDateFrom && matchDateTo && matchCapacityFlag(item);
    });
  }, [programmeCapacityFlag, programmeCoach, programmeDateFrom, programmeDateTo, programmeSearch, programmeStatus, programmes]);

  const filteredPrivateLessons = useMemo(() => {
    const searchText = privateSearch.trim().toLowerCase();
    return privateLessons.filter((item) => {
      const matchSearch = !searchText
        || item.customer_name.toLowerCase().includes(searchText)
        || (item.customer_phone || '').toLowerCase().includes(searchText)
        || (item.coach_name || '').toLowerCase().includes(searchText);
      const matchStatus = privateStatus === 'all' || item.status === privateStatus;
      return matchSearch && matchStatus;
    });
  }, [privateLessons, privateSearch, privateStatus]);
  const isSelectedProgrammeEnrollmentBlocked = isProgrammeEnrollmentBlocked(selectedProgramme?.status || null);
  const enrollmentFormDisabled = savingEnrollment || isSelectedProgrammeEnrollmentBlocked;

  const trimmedProgrammeSearch = useMemo(() => programmeSearch.trim(), [programmeSearch]);
  const trimmedProgrammeCoach = useMemo(() => programmeCoach.trim(), [programmeCoach]);

  const programmeApiFilters = useMemo(() => ({
    status: (programmeStatus !== 'all' && programmeStatus !== 'active') ? programmeStatus : undefined,
    start_date_from: programmeDateFrom || undefined,
    start_date_to: programmeDateTo || undefined,
    coach_name: trimmedProgrammeCoach || undefined,
    capacity_flag: programmeCapacityFlag !== 'all' ? programmeCapacityFlag : undefined,
  }), [programmeCapacityFlag, programmeDateFrom, programmeDateTo, programmeStatus, trimmedProgrammeCoach]);

  const programmeCalendarQuery = useMemo(() => ({
    start: programmeCalendar.range.start,
    end: programmeCalendar.range.end,
    search: trimmedProgrammeSearch || undefined,
    ...programmeApiFilters,
  }), [
    programmeApiFilters,
    programmeCalendar.range.end,
    programmeCalendar.range.start,
    trimmedProgrammeSearch,
  ]);

  const programmeFilterChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; onRemove: () => void }> = [];
    if (programmeStatus !== DEFAULT_PROGRAMME_STATUS_FILTER) {
      const statusLabel = programmeStatus === 'all' ? '全部状态（含已取消）' : programmeStatus;
      chips.push({
        key: 'status',
        label: `状态: ${statusLabel}`,
        onRemove: () => setProgrammeStatus(DEFAULT_PROGRAMME_STATUS_FILTER),
      });
    }
    if (programmeDateFrom) {
      chips.push({
        key: 'date_from',
        label: `开始 >= ${programmeDateFrom}`,
        onRemove: () => setProgrammeDateFrom(''),
      });
    }
    if (programmeDateTo) {
      chips.push({
        key: 'date_to',
        label: `结束 <= ${programmeDateTo}`,
        onRemove: () => setProgrammeDateTo(''),
      });
    }
    if (trimmedProgrammeCoach) {
      chips.push({
        key: 'coach',
        label: `教练: ${trimmedProgrammeCoach}`,
        onRemove: () => setProgrammeCoach(''),
      });
    }
    if (programmeCapacityFlag !== 'all') {
      chips.push({
        key: 'capacity',
        label: `容量: ${programmeCapacityFlag}`,
        onRemove: () => setProgrammeCapacityFlag('all'),
      });
    }
    return chips;
  }, [programmeCapacityFlag, programmeDateFrom, programmeDateTo, programmeStatus, trimmedProgrammeCoach]);

  const clearProgrammeFilters = useCallback(() => {
    setProgrammeSearch('');
    setProgrammeStatus(DEFAULT_PROGRAMME_STATUS_FILTER);
    setProgrammeDateFrom('');
    setProgrammeDateTo('');
    setProgrammeCoach('');
    setProgrammeCapacityFlag('all');
  }, []);

  const setTransientHighlight = useCallback((key: AssistantUiEffectHighlightKey, value: number | null, durationMs = 2600) => {
    if (!value || value <= 0) return;
    const nextToken = {
      key,
      value,
      expiresAt: Date.now() + Math.max(600, durationMs),
    };
    setHighlightToken(nextToken);
    if (highlightTimerRef.current) {
      window.clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    highlightTimerRef.current = window.setTimeout(() => {
      setHighlightToken((current) => {
        if (!current) return null;
        if (current.key !== nextToken.key || current.value !== nextToken.value) return current;
        return null;
      });
      highlightTimerRef.current = null;
    }, Math.max(600, durationMs));
  }, []);

  const isHighlighted = useCallback((key: AssistantUiEffectHighlightKey, value: number | null | undefined) => {
    if (!highlightToken || !value) return false;
    if (Date.now() > highlightToken.expiresAt) return false;
    return highlightToken.key === key && Number(highlightToken.value) === Number(value);
  }, [highlightToken]);

  const scrollToEntity = useCallback((key: AssistantUiEffectHighlightKey, value: number | null) => {
    if (!value || value <= 0) return;
    const selectors: Record<AssistantUiEffectHighlightKey, string> = {
      programme_id: `[data-programme-id="${value}"]`,
      session_id: `[data-session-id="${value}"]`,
      enrollment_id: `[data-enrollment-id="${value}"]`,
      private_lesson_id: `[data-private-lesson-id="${value}"]`,
    };

    window.setTimeout(() => {
      const element = document.querySelector(selectors[key]);
      if (!element) return;
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 200);
  }, []);

  useEffect(() => {
    const requested = searchParams.get('view') as ProgrammeCenterView | null;
    const next = requested && availableViews.includes(requested)
      ? requested
      : availableViews[0];

    if (next && next !== activeTab) {
      setActiveTab(next);
    }
  }, [activeTab, availableViews, searchParams]);

  const handleViewChange = useCallback((view: ProgrammeCenterView) => {
    setActiveTab(view);

    const current = searchParams.get('view');
    const expected = view === 'programme' ? null : view;
    if ((current || null) === expected) return;

    const nextSearch = new URLSearchParams(searchParams);
    if (expected) {
      nextSearch.set('view', expected);
    } else {
      nextSearch.delete('view');
    }
    setSearchParams(nextSearch, { replace: true });
  }, [searchParams, setSearchParams]);

  const buildContentHeaders = useCallback(() => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const token = localStorage.getItem('staff_token');
    if (token) {
      headers['X-Staff-Token'] = token;
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }, []);

  const loadContentProgrammeProfiles = useCallback(async () => {
    if (!canManageContent) return;
    try {
      const response = await fetch(getContentApiUrl('programmes'), {
        headers: buildContentHeaders(),
      });
      if (!response.ok) return;

      const data = await response.json();
      if (!Array.isArray(data)) return;

      setContentProgrammeProfiles(
        data
          .map((item) => ({
            id: String(item?.id || '').trim(),
            title: String(item?.title || '').trim(),
          }))
          .filter((item) => item.id.length > 0)
      );
    } catch {
      // keep UI functional even when content profiles endpoint is temporarily unavailable
    }
  }, [buildContentHeaders, canManageContent]);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      if (canManageProgrammes || canManagePrivateLessons) {
        const [overviewRes, programmeRes, privateRes, locationRes, courtRes] = await Promise.all([
          api.scheduling.getOverview(),
          api.scheduling.getProgrammes(programmeApiFilters),
          api.scheduling.getPrivateLessons(),
          api.courts.getLocations(),
          api.courts.getCourts(),
        ]);

        if (!overviewRes.error && overviewRes.data) {
          setOverview((overviewRes.data as SchedulingOverview) || defaultOverview);
        }
        if (!programmeRes.error && programmeRes.data) {
          setProgrammes((programmeRes.data as { programmes: Programme[] }).programmes || []);
        }
        if (!privateRes.error && privateRes.data) {
          setPrivateLessons((privateRes.data as { private_lessons: PrivateLesson[] }).private_lessons || []);
        }
        if (!locationRes.error && Array.isArray(locationRes.data)) {
          setLocations(locationRes.data as Location[]);
        }
        if (!courtRes.error && Array.isArray(courtRes.data)) {
          setCourts(courtRes.data as Court[]);
        }
      } else {
        setOverview(defaultOverview);
        setProgrammes([]);
        setPrivateLessons([]);
        setLocations([]);
        setCourts([]);
      }

      if (canManageContent) {
        await loadContentProgrammeProfiles();
      } else {
        setContentProgrammeProfiles([]);
      }
    } finally {
      setLoading(false);
    }
  }, [
    canManageContent,
    canManagePrivateLessons,
    canManageProgrammes,
    loadContentProgrammeProfiles,
    programmeApiFilters,
  ]);

  const loadProgrammeCalendar = useCallback(async () => {
    if (!canManageProgrammes || activeTab !== 'programme' || programmeDisplayMode !== 'calendar') return;
    setProgrammeCalendarLoading(true);
    try {
      const result = await api.scheduling.getProgrammeCalendar(programmeCalendarQuery);

      if (result.error || !result.data) {
        toast.error(result.error || '加载日历视图失败');
        return;
      }
      const payload = result.data as ProgrammeCalendarResponse;
      const filteredItems = Array.isArray(payload.items) && programmeStatus === 'active'
        ? payload.items.filter((item) => String(item?.programme?.status || '').toLowerCase() !== 'cancelled')
        : (payload.items || []);
      setProgrammeCalendar({
        ...payload,
        items: filteredItems,
      });
    } finally {
      setProgrammeCalendarLoading(false);
    }
  }, [
    activeTab,
    canManageProgrammes,
    programmeStatus,
    programmeCalendarQuery,
    programmeDisplayMode,
  ]);

  const loadProgrammeDetail = useCallback(async (programmeId: number) => {
    const result = await api.scheduling.getProgrammeDetail(programmeId);
    if (result.error || !result.data) {
      toast.error(result.error || '加载训练营详情失败');
      return;
    }

    const payload = result.data as {
      programme: Programme;
      sessions: ProgrammeSession[];
      enrollments: ProgrammeEnrollment[];
    };
    setSelectedProgramme(payload.programme);
    setProgrammeDetail({
      sessions: payload.sessions || [],
      enrollments: payload.enrollments || [],
    });
    setProgrammeDetailOpen(true);
  }, []);

  const loadPrivateLessonDetail = useCallback(async (lessonId: number) => {
    const result = await api.scheduling.getPrivateLessonDetail(lessonId);
    if (result.error || !result.data) {
      toast.error(result.error || '加载私教课详情失败');
      return;
    }

    const payload = result.data as { private_lesson: PrivateLesson; participants: PrivateLessonParticipant[] };
    setSelectedPrivateLesson(payload.private_lesson);
    setPrivateLessonDetail({ participants: payload.participants || [] });
    setPrivateDetailOpen(true);
  }, []);

  const refreshProgrammePanels = useCallback(async (programmeId: number) => {
    await loadProgrammeDetail(programmeId);
    await loadDashboardData();
  }, [loadDashboardData, loadProgrammeDetail]);

  const highlightEnrollmentRow = useCallback((enrollmentId: number, durationMs = 2600) => {
    setTransientHighlight('enrollment_id', enrollmentId, durationMs);
    scrollToEntity('enrollment_id', enrollmentId);
  }, [scrollToEntity, setTransientHighlight]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    void loadProgrammeCalendar();
  }, [loadProgrammeCalendar]);

  useEffect(() => {
    const element = programmeTableRef.current;
    if (!element) {
      setShowProgrammeScrollHint(false);
      return;
    }

    const updateHint = () => {
      const canScroll = element.scrollWidth > element.clientWidth + 6;
      const notAtEnd = element.scrollLeft + element.clientWidth < element.scrollWidth - 6;
      setShowProgrammeScrollHint(canScroll && notAtEnd);
    };

    updateHint();
    element.addEventListener('scroll', updateHint, { passive: true });
    window.addEventListener('resize', updateHint);
    return () => {
      element.removeEventListener('scroll', updateHint);
      window.removeEventListener('resize', updateHint);
    };
  }, [filteredProgrammes.length, activeTab, programmeDisplayMode]);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        window.clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }
    };
  }, []);

  const openCreateProgrammeModal = () => {
    setProgrammeForm(makeDefaultProgrammeForm());
    setProgrammeModalOpen(true);
  };

  const openEditProgrammeModal = (programme: Programme) => {
    setProgrammeForm({
      id: programme.id,
      title: programme.title,
      description: programme.description || '',
      level: programme.level,
      location_id: programme.location_id ? String(programme.location_id) : '',
      coach_name: programme.coach_name || '',
      start_date: programme.start_date,
      end_date: programme.end_date,
      start_time: programme.start_time ? programme.start_time.slice(0, 5) : '18:00',
      end_time: programme.end_time ? programme.end_time.slice(0, 5) : '19:00',
      min_participants: programme.min_participants,
      max_participants: programme.max_participants,
      content_programme_id: programme.content_programme_id || '',
      status: programme.status,
      notes: programme.notes || '',
    });
    setProgrammeModalOpen(true);
  };

  const saveProgramme = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingProgramme(true);

    const payload = {
      title: programmeForm.title,
      description: programmeForm.description || null,
      level: programmeForm.level,
      location_id: programmeForm.location_id ? Number(programmeForm.location_id) : null,
      coach_name: programmeForm.coach_name || null,
      start_date: programmeForm.start_date,
      end_date: programmeForm.end_date,
      start_time: programmeForm.start_time ? `${programmeForm.start_time}:00` : null,
      end_time: programmeForm.end_time ? `${programmeForm.end_time}:00` : null,
      min_participants: Number(programmeForm.min_participants),
      max_participants: Number(programmeForm.max_participants),
      content_programme_id: programmeForm.content_programme_id || null,
      status: programmeForm.status,
      notes: programmeForm.notes || null,
      days_of_week: [],
    };

    try {
      const result = programmeForm.id
        ? await api.scheduling.updateProgramme(programmeForm.id, payload)
        : await api.scheduling.createProgramme(payload);

      if (result.error) {
        toast.error(result.error);
        return;
      }

      toast.success(programmeForm.id ? '训练营已更新' : '训练营已创建');
      setProgrammeModalOpen(false);
      await loadDashboardData();
    } finally {
      setSavingProgramme(false);
    }
  };

  const addSession = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedProgramme) return;

    const result = await api.scheduling.addProgrammeSession(selectedProgramme.id, {
      session_date: sessionForm.session_date,
      start_time: `${sessionForm.start_time}:00`,
      end_time: `${sessionForm.end_time}:00`,
      court_id: sessionForm.court_id ? Number(sessionForm.court_id) : null,
      capacity: sessionForm.capacity ? Number(sessionForm.capacity) : null,
      notes: sessionForm.notes || null,
    });

    if (result.error) {
      toast.error(result.error);
      return;
    }

    toast.success('课次已添加');
    setSessionForm({
      session_date: new Date().toISOString().slice(0, 10),
      start_time: '18:00',
      end_time: '19:00',
      court_id: '',
      capacity: '',
      notes: '',
    });
    await refreshProgrammePanels(selectedProgramme.id);
  };

  const addEnrollment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedProgramme) return;
    if (isProgrammeEnrollmentBlocked(selectedProgramme.status)) {
      toast.error(`当前训练营状态为 ${selectedProgramme.status}，不能新增报名`);
      return;
    }

    setSavingEnrollment(true);
    try {
      const result = await api.scheduling.addProgrammeEnrollment(selectedProgramme.id, {
        customer_name: enrollmentForm.customer_name,
        customer_email: enrollmentForm.customer_email || null,
        customer_phone: enrollmentForm.customer_phone || null,
        status: enrollmentForm.status,
        auto_waitlist: enrollmentForm.auto_waitlist,
      });

      if (result.error) {
        toast.error(result.error);
        return;
      }

      toast.success('学员已录入');
      setEnrollmentForm({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        status: 'active',
        auto_waitlist: true,
      });
      await refreshProgrammePanels(selectedProgramme.id);
      const enrollmentId = Number((result.data as { enrollment?: { id?: number } } | undefined)?.enrollment?.id || 0) || null;
      if (enrollmentId) {
        highlightEnrollmentRow(enrollmentId, 3000);
      }
    } finally {
      setSavingEnrollment(false);
    }
  };

  const changeEnrollmentStatus = async (enrollment: ProgrammeEnrollment, status: ProgrammeEnrollment['status']) => {
    if (!selectedProgramme) return;

    const result = await api.scheduling.updateProgrammeEnrollment(selectedProgramme.id, enrollment.id, {
      status,
      exit_reason: ['withdrawn', 'cancelled'].includes(status) ? 'Staff updated in scheduler' : null,
    });

    if (result.error) {
      toast.error(result.error);
      return;
    }

    toast.success('报名状态已更新');
    await refreshProgrammePanels(selectedProgramme.id);
    highlightEnrollmentRow(enrollment.id, 2600);
  };

  const deleteEnrollment = async (enrollment: ProgrammeEnrollment) => {
    if (!selectedProgramme) return;

    const confirmed = window.confirm(`确认删除 ${enrollment.customer_name} 的报名记录吗？该操作不可恢复。`);
    if (!confirmed) return;

    const result = await api.scheduling.deleteProgrammeEnrollment(selectedProgramme.id, enrollment.id);
    if (result.error) {
      toast.error(result.error);
      return;
    }

    toast.success('报名记录已删除');
    await refreshProgrammePanels(selectedProgramme.id);
  };

  const exportProgrammeEnrollments = useCallback(async (programme: Programme) => {
    const result = await api.scheduling.getProgrammeEnrollmentReport(programme.id, {
      format: 'both',
      status: 'active',
    });
    if (result.error || !result.data) {
      toast.error(result.error || '导出报名名单失败');
      return;
    }

    const payload = result.data;
    const reports = Array.isArray(payload.reports) ? payload.reports : [];
    if (reports.length === 0) {
      toast.error('没有可下载的报表文件');
      return;
    }

    const links = reports
      .map((item) => item.public_url)
      .filter((item): item is string => Boolean(item && item.trim()));
    if (links[0]) {
      window.open(links[0], '_blank', 'noopener,noreferrer');
    }
    toast.success(`${programme.title} 报名名单已生成（CSV/PDF）`);
  }, []);

  const shiftCalendarWeek = useCallback((offsetDays: number) => {
    setProgrammeCalendar((prev) => {
      const start = shiftDate(prev.range.start, offsetDays);
      const end = shiftDate(prev.range.end, offsetDays);
      return {
        ...prev,
        range: { start, end },
      };
    });
  }, []);

  const goToCurrentWeek = useCallback(() => {
    const start = getCurrentWeekStart();
    const end = shiftDate(start, 6);
    setProgrammeCalendar((prev) => ({
      ...prev,
      range: { start, end },
    }));
  }, []);

  const openCreatePrivateModal = () => {
    setPrivateForm(makeDefaultPrivateLessonForm());
    setPrivateModalOpen(true);
  };

  const openEditPrivateModal = (lesson: PrivateLesson) => {
    setPrivateForm({
      id: lesson.id,
      title: lesson.title,
      customer_name: lesson.customer_name,
      customer_email: lesson.customer_email || '',
      customer_phone: lesson.customer_phone || '',
      participant_limit: lesson.participant_limit,
      date: lesson.date,
      start_time: lesson.start_time ? lesson.start_time.slice(0, 5) : '18:00',
      end_time: lesson.end_time ? lesson.end_time.slice(0, 5) : '19:00',
      is_flexible_time: Boolean(lesson.is_flexible_time),
      location_id: lesson.location_id ? String(lesson.location_id) : '',
      court_id: lesson.court_id ? String(lesson.court_id) : '',
      coach_name: lesson.coach_name || '',
      status: lesson.status,
      notes: lesson.notes || '',
    });
    setPrivateModalOpen(true);
  };

  const savePrivateLesson = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingPrivateLesson(true);

    const payload = {
      title: privateForm.title,
      customer_name: privateForm.customer_name,
      customer_email: privateForm.customer_email || null,
      customer_phone: privateForm.customer_phone || null,
      participant_limit: Number(privateForm.participant_limit),
      date: privateForm.date,
      start_time: privateForm.is_flexible_time ? null : `${privateForm.start_time}:00`,
      end_time: privateForm.is_flexible_time ? null : `${privateForm.end_time}:00`,
      is_flexible_time: privateForm.is_flexible_time,
      location_id: privateForm.location_id ? Number(privateForm.location_id) : null,
      court_id: privateForm.court_id ? Number(privateForm.court_id) : null,
      coach_name: privateForm.coach_name || null,
      status: privateForm.status,
      notes: privateForm.notes || null,
    };

    try {
      const result = privateForm.id
        ? await api.scheduling.updatePrivateLesson(privateForm.id, payload)
        : await api.scheduling.createPrivateLesson(payload);

      if (result.error) {
        toast.error(result.error);
        return;
      }

      toast.success(privateForm.id ? '私教课已更新' : '私教课已创建');
      setPrivateModalOpen(false);
      await loadDashboardData();
    } finally {
      setSavingPrivateLesson(false);
    }
  };

  const addPrivateParticipant = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedPrivateLesson) return;

    const result = await api.scheduling.addPrivateLessonParticipant(selectedPrivateLesson.id, {
      participant_name: participantForm.participant_name,
      participant_email: participantForm.participant_email || null,
      participant_phone: participantForm.participant_phone || null,
    });

    if (result.error) {
      toast.error(result.error);
      return;
    }

    toast.success('参与人已添加');
    setParticipantForm({ participant_name: '', participant_email: '', participant_phone: '' });
    await loadPrivateLessonDetail(selectedPrivateLesson.id);
    await loadDashboardData();
  };

  const updateParticipantStatus = async (
    participant: PrivateLessonParticipant,
    status: PrivateLessonParticipant['status']
  ) => {
    if (!selectedPrivateLesson) return;

    const result = await api.scheduling.updatePrivateLessonParticipant(selectedPrivateLesson.id, participant.id, { status });
    if (result.error) {
      toast.error(result.error);
      return;
    }

    toast.success('参与人状态已更新');
    await loadPrivateLessonDetail(selectedPrivateLesson.id);
    await loadDashboardData();
  };

  return (
    <ProtectedComponent permissions={[Permission.MANAGE_PROGRAMMES, Permission.MANAGE_PRIVATE_LESSONS, Permission.MANAGE_CONTENT]}>
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <GraduationCap className="h-8 w-8 text-primary-600" />
              课程中心
            </h1>
            <p className="text-gray-500 mt-1">
              Staff 专用：统一管理训练营与小课的排课、课程资料和弹窗/相册图
            </p>
          </div>

          <div className="flex items-center gap-2">
            {activeTab === 'programme' && canManageProgrammes ? (
              <button className="btn-primary gap-2" onClick={openCreateProgrammeModal}>
                <Plus className="h-4 w-4" />
                新建训练营
              </button>
            ) : null}
            {activeTab === 'private' && canManagePrivateLessons ? (
              <button className="btn-primary gap-2" onClick={openCreatePrivateModal}>
                <Plus className="h-4 w-4" />
                新建私教课
              </button>
            ) : null}

            <button className="btn-secondary gap-2" onClick={loadDashboardData} disabled={loading}>
              <RefreshCw className={clsx('h-4 w-4', loading && 'animate-spin')} />
              刷新
            </button>
          </div>
        </div>

        {(canManageProgrammes || canManagePrivateLessons) ? (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="card card-compact">
              <p className="text-xs uppercase tracking-wide text-gray-500">训练营总数</p>
              <p className="text-2xl font-bold mt-1">{overview.summary.programmes_total}</p>
            </div>
            <div className="card card-compact">
              <p className="text-xs uppercase tracking-wide text-gray-500">可开班训练营</p>
              <p className="text-2xl font-bold mt-1">{overview.summary.programmes_open}</p>
            </div>
            <div className="card card-compact">
              <p className="text-xs uppercase tracking-wide text-gray-500">活跃报名</p>
              <p className="text-2xl font-bold mt-1">{overview.summary.active_enrollments}</p>
            </div>
            <div className="card card-compact">
              <p className="text-xs uppercase tracking-wide text-gray-500">今日私教课</p>
              <p className="text-2xl font-bold mt-1">{overview.summary.private_lessons_today}</p>
            </div>
            <div className="card card-compact">
              <p className="text-xs uppercase tracking-wide text-gray-500">待确认私教</p>
              <p className="text-2xl font-bold mt-1">{overview.summary.private_lessons_pending}</p>
            </div>
          </div>
        ) : null}

        <div className="card card-compact">
          <div className="inline-flex rounded-xl border border-gray-200 p-1 bg-gray-50">
            {availableViews.map((view) => (
              <button
                key={view}
                className={clsx(
                  'px-4 py-2 text-sm rounded-lg transition-colors',
                  activeTab === view ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-600'
                )}
                onClick={() => handleViewChange(view)}
              >
                {view === 'programme' ? '训练营' : null}
                {view === 'private' ? '小课' : null}
                {view === 'profiles' ? '课程资料' : null}
                {view === 'promotion' ? '弹窗和相册图' : null}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'programme' && canManageProgrammes ? (
          <div className="card card-comfortable space-y-4">
            <div className="flex flex-col lg:flex-row gap-3">
              <input
                className="input"
                placeholder="搜索训练营标题/编号/教练"
                value={programmeSearch}
                onChange={(event) => setProgrammeSearch(event.target.value)}
              />
                <select
                  className="input lg:w-48"
                  value={programmeStatus}
                  onChange={(event) => setProgrammeStatus(event.target.value as ProgrammeStatusFilter)}
                >
                  <option value="active">运营中（默认）</option>
                  <option value="all">全部状态（含已取消）</option>
                  {PROGRAMME_STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                  </option>
                ))}
              </select>
              <input
                type="date"
                className="input lg:w-44"
                value={programmeDateFrom}
                onChange={(event) => setProgrammeDateFrom(event.target.value)}
              />
              <input
                type="date"
                className="input lg:w-44"
                value={programmeDateTo}
                onChange={(event) => setProgrammeDateTo(event.target.value)}
              />
              <input
                className="input lg:w-44"
                placeholder="教练名"
                value={programmeCoach}
                onChange={(event) => setProgrammeCoach(event.target.value)}
              />
              <select
                className="input lg:w-48"
                value={programmeCapacityFlag}
                onChange={(event) => setProgrammeCapacityFlag(event.target.value as 'all' | ProgrammeCapacityFlag)}
              >
                <option value="all">容量: 全部</option>
                <option value="under_min">容量: 未达最小开班</option>
                <option value="near_full">容量: 接近满员</option>
                <option value="full">容量: 已满员</option>
              </select>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {programmeFilterChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs text-blue-700"
                  onClick={chip.onRemove}
                >
                  {chip.label}
                  <X className="h-3 w-3" />
                </button>
              ))}
              {programmeFilterChips.length > 0 ? (
                <button type="button" className="btn-secondary btn-sm" onClick={clearProgrammeFilters}>
                  清空筛选
                </button>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                <button
                  type="button"
                  className={clsx(
                    'px-3 py-1.5 text-sm rounded-md',
                    programmeDisplayMode === 'list' ? 'bg-white shadow-sm text-primary-700' : 'text-gray-600'
                  )}
                  onClick={() => setProgrammeDisplayMode('list')}
                >
                  列表
                </button>
                <button
                  type="button"
                  className={clsx(
                    'px-3 py-1.5 text-sm rounded-md',
                    programmeDisplayMode === 'calendar' ? 'bg-white shadow-sm text-primary-700' : 'text-gray-600'
                  )}
                  onClick={() => setProgrammeDisplayMode('calendar')}
                >
                  日历（周）
                </button>
              </div>

              {programmeDisplayMode === 'calendar' ? (
                <div className="inline-flex items-center gap-1">
                  <button type="button" className="btn-secondary btn-sm" onClick={() => shiftCalendarWeek(-7)}>
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button type="button" className="btn-secondary btn-sm" onClick={goToCurrentWeek}>
                    本周
                  </button>
                  <button type="button" className="btn-secondary btn-sm" onClick={() => shiftCalendarWeek(7)}>
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              ) : null}
            </div>

            {programmeDisplayMode === 'list' ? (
              <div className="programme-table-shell">
                <div className={clsx('programme-scroll-hint', !showProgrammeScrollHint && 'opacity-0 pointer-events-none')}>
                  向右查看更多
                </div>
                <div ref={programmeTableRef} className="overflow-x-auto relative">
                  <table className="table min-w-[1180px]">
                    <thead className="table-header">
                      <tr>
                        <th className="table-header-cell programme-sticky-left min-w-[280px]">训练营</th>
                        <th className="table-header-cell min-w-[170px]">日期</th>
                        <th className="table-header-cell min-w-[130px]">教练</th>
                        <th className="table-header-cell min-w-[140px]">容量</th>
                        <th className="table-header-cell min-w-[180px]">绑定与备注</th>
                        <th className="table-header-cell programme-sticky-right-status">状态</th>
                        <th className="table-header-cell programme-sticky-right-actions">操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProgrammes.map((programme) => (
                        <tr
                          key={programme.id}
                          data-programme-id={programme.id}
                          className={clsx(
                            'table-row',
                            programme.status === 'cancelled' && 'opacity-70',
                            isHighlighted('programme_id', programme.id) && 'bg-primary-50/70 ring-2 ring-primary-200 animate-pulse'
                          )}
                        >
                          <td className="table-cell programme-sticky-left">
                            <div className="font-mono text-[11px] text-gray-500">{programme.code}</div>
                            <div className="flex items-center gap-2">
                              <div className="font-semibold text-gray-900">{programme.title}</div>
                              {programme.status === 'cancelled' ? (
                                <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                                  已取消
                                </span>
                              ) : null}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">{programme.description || '无描述'}</div>
                          </td>
                          <td className="table-cell text-sm">
                            {programme.start_date} ~ {programme.end_date}
                          </td>
                          <td className="table-cell">{programme.coach_name || '未安排'}</td>
                          <td className="table-cell text-sm">
                            <div>{programme.enrolled_count}/{programme.max_participants}</div>
                            <div className="text-xs text-gray-500">最小开班: {programme.min_participants}</div>
                          </td>
                          <td className="table-cell text-sm">
                            <div className="text-primary-700">绑定内容: {programme.content_programme_id || '未绑定'}</div>
                            <div className="text-xs text-gray-500 mt-1 truncate max-w-[240px]">{programme.notes || '无备注'}</div>
                          </td>
                          <td className="table-cell programme-sticky-right-status">
                            <span className={clsx('badge', STATUS_COLOR[programme.status] || 'badge-gray')}>{programme.status}</span>
                          </td>
                          <td className="table-cell programme-sticky-right-actions">
                            <div className="flex items-center gap-1.5">
                              <button className="btn-secondary btn-sm" onClick={() => loadProgrammeDetail(programme.id)}>
                                详情
                              </button>
                              <button className="btn-secondary btn-sm" onClick={() => openEditProgrammeModal(programme)}>
                                编辑
                              </button>
                              <button className="btn-secondary btn-sm" onClick={() => exportProgrammeEnrollments(programme)}>
                                报名名单
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <ProgrammeCalendar
                items={programmeCalendar.items || []}
                rangeStart={programmeCalendar.range.start}
                rangeEnd={programmeCalendar.range.end}
                loading={programmeCalendarLoading}
              />
            )}

            {programmeDisplayMode === 'list' && filteredProgrammes.length === 0 ? (
              <div className="text-sm text-gray-500 py-8 text-center">
                当前筛选条件下没有训练营结果。
              </div>
            ) : null}

            {programmeDisplayMode === 'calendar' && !programmeCalendarLoading && (programmeCalendar.items || []).length === 0 ? (
              <div className="text-sm text-gray-500 py-8 text-center">
                当前筛选条件下没有可展示的周日历课次。
              </div>
            ) : null}
          </div>
        ) : null}

        {activeTab === 'private' && canManagePrivateLessons ? (
          <div className="card card-comfortable space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                className="input"
                placeholder="搜索客户/电话/教练"
                value={privateSearch}
                onChange={(event) => setPrivateSearch(event.target.value)}
              />
              <select
                className="input sm:w-56"
                value={privateStatus}
                onChange={(event) => setPrivateStatus(event.target.value)}
              >
                <option value="all">全部状态</option>
                {PRIVATE_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">日期时间</th>
                    <th className="table-header-cell">客户</th>
                    <th className="table-header-cell">教练</th>
                    <th className="table-header-cell">人数</th>
                    <th className="table-header-cell">状态</th>
                    <th className="table-header-cell">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPrivateLessons.map((lesson) => (
                    <tr
                      key={lesson.id}
                      data-private-lesson-id={lesson.id}
                      className={clsx(
                        'table-row',
                        isHighlighted('private_lesson_id', lesson.id) && 'bg-primary-50/70 ring-2 ring-primary-200 animate-pulse'
                      )}
                    >
                      <td className="table-cell">
                        <div>{lesson.date}</div>
                        <div className="text-xs text-gray-500">
                          {lesson.is_flexible_time ? '灵活时间' : `${lesson.start_time?.slice(0, 5)} - ${lesson.end_time?.slice(0, 5)}`}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="font-semibold">{lesson.customer_name}</div>
                        <div className="text-xs text-gray-500">{lesson.customer_phone || lesson.customer_email || '无联系方式'}</div>
                      </td>
                      <td className="table-cell">{lesson.coach_name || '未安排'}</td>
                      <td className="table-cell">{lesson.participant_count}/{lesson.participant_limit}</td>
                      <td className="table-cell">
                        <span className={clsx('badge', STATUS_COLOR[lesson.status] || 'badge-gray')}>{lesson.status}</span>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <button className="btn-secondary btn-sm" onClick={() => loadPrivateLessonDetail(lesson.id)}>
                            详情
                          </button>
                          <button className="btn-secondary btn-sm" onClick={() => openEditPrivateModal(lesson)}>
                            编辑
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {activeTab === 'profiles' && canManageContent ? (
          <div className="card card-comfortable">
            <ContentManagement mode="programmeOnly" embedded />
          </div>
        ) : null}

        {activeTab === 'promotion' && canManageContent ? (
          <div className="card card-comfortable">
            <ContentManagement mode="marketingOnly" embedded />
          </div>
        ) : null}

        <Modal isOpen={programmeModalOpen} onClose={() => setProgrammeModalOpen(false)} title={programmeForm.id ? '编辑训练营' : '新建训练营'}>
          <form className="space-y-4" onSubmit={saveProgramme}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">标题</label>
                <input className="input" value={programmeForm.title} onChange={(event) => setProgrammeForm((prev) => ({ ...prev, title: event.target.value }))} required />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">描述</label>
                <textarea
                  className="input min-h-[96px]"
                  value={programmeForm.description}
                  onChange={(event) => setProgrammeForm((prev) => ({ ...prev, description: event.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">级别</label>
                <select
                  className="input"
                  value={programmeForm.level}
                  onChange={(event) => setProgrammeForm((prev) => ({ ...prev, level: event.target.value as Programme['level'] }))}
                >
                  <option value="mixed">mixed</option>
                  <option value="beginner">beginner</option>
                  <option value="intermediate">intermediate</option>
                  <option value="advanced">advanced</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">状态</label>
                <select
                  className="input"
                  value={programmeForm.status}
                  onChange={(event) => setProgrammeForm((prev) => ({ ...prev, status: event.target.value as Programme['status'] }))}
                >
                  {PROGRAMME_STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">场馆</label>
                <select
                  className="input"
                  value={programmeForm.location_id}
                  onChange={(event) => setProgrammeForm((prev) => ({ ...prev, location_id: event.target.value }))}
                >
                  <option value="">未指定</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>{location.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">教练</label>
                <input className="input" value={programmeForm.coach_name} onChange={(event) => setProgrammeForm((prev) => ({ ...prev, coach_name: event.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">开始日期</label>
                <input
                  type="date"
                  className="input"
                  value={programmeForm.start_date}
                  onChange={(event) => setProgrammeForm((prev) => ({ ...prev, start_date: event.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">结束日期</label>
                <input
                  type="date"
                  className="input"
                  value={programmeForm.end_date}
                  onChange={(event) => setProgrammeForm((prev) => ({ ...prev, end_date: event.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">开始时间</label>
                <input
                  type="time"
                  className="input"
                  value={programmeForm.start_time}
                  onChange={(event) => setProgrammeForm((prev) => ({ ...prev, start_time: event.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">结束时间</label>
                <input
                  type="time"
                  className="input"
                  value={programmeForm.end_time}
                  onChange={(event) => setProgrammeForm((prev) => ({ ...prev, end_time: event.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">最小开班人数</label>
                <input
                  type="number"
                  min={1}
                  className="input"
                  value={programmeForm.min_participants}
                  onChange={(event) => setProgrammeForm((prev) => ({ ...prev, min_participants: Number(event.target.value) }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">最大人数</label>
                <input
                  type="number"
                  min={1}
                  className="input"
                  value={programmeForm.max_participants}
                  onChange={(event) => setProgrammeForm((prev) => ({ ...prev, max_participants: Number(event.target.value) }))}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">绑定课程资料（content programme profile）</label>
                <select
                  className="input"
                  value={programmeForm.content_programme_id}
                  onChange={(event) => setProgrammeForm((prev) => ({ ...prev, content_programme_id: event.target.value }))}
                >
                  <option value="">未绑定</option>
                  {contentProgrammeProfiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {profile.title || profile.id} ({profile.id})
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  绑定后，主站课程页将按“运营中 + 已绑定”规则展示。
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">备注</label>
                <textarea className="input min-h-[96px]" value={programmeForm.notes} onChange={(event) => setProgrammeForm((prev) => ({ ...prev, notes: event.target.value }))} />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-secondary" onClick={() => setProgrammeModalOpen(false)}>
                取消
              </button>
              <button type="submit" className="btn-primary" disabled={savingProgramme}>
                {savingProgramme ? '保存中...' : '保存'}
              </button>
            </div>
          </form>
        </Modal>

        <Modal isOpen={privateModalOpen} onClose={() => setPrivateModalOpen(false)} title={privateForm.id ? '编辑私教课' : '新建私教课'}>
          <form className="space-y-4" onSubmit={savePrivateLesson}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">标题</label>
                <input className="input" value={privateForm.title} onChange={(event) => setPrivateForm((prev) => ({ ...prev, title: event.target.value }))} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">客户姓名</label>
                <input className="input" value={privateForm.customer_name} onChange={(event) => setPrivateForm((prev) => ({ ...prev, customer_name: event.target.value }))} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">教练</label>
                <input className="input" value={privateForm.coach_name} onChange={(event) => setPrivateForm((prev) => ({ ...prev, coach_name: event.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">邮箱</label>
                <input type="email" className="input" value={privateForm.customer_email} onChange={(event) => setPrivateForm((prev) => ({ ...prev, customer_email: event.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">电话</label>
                <input className="input" value={privateForm.customer_phone} onChange={(event) => setPrivateForm((prev) => ({ ...prev, customer_phone: event.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">日期</label>
                <input type="date" className="input" value={privateForm.date} onChange={(event) => setPrivateForm((prev) => ({ ...prev, date: event.target.value }))} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">状态</label>
                <select className="input" value={privateForm.status} onChange={(event) => setPrivateForm((prev) => ({ ...prev, status: event.target.value as PrivateLesson['status'] }))}>
                  {PRIVATE_STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2 flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
                <input
                  id="private-flexible"
                  type="checkbox"
                  checked={privateForm.is_flexible_time}
                  onChange={(event) => setPrivateForm((prev) => ({ ...prev, is_flexible_time: event.target.checked }))}
                />
                <label htmlFor="private-flexible" className="text-sm">灵活时间（不占固定时段）</label>
              </div>
              {!privateForm.is_flexible_time && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">开始时间</label>
                    <input type="time" className="input" value={privateForm.start_time} onChange={(event) => setPrivateForm((prev) => ({ ...prev, start_time: event.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">结束时间</label>
                    <input type="time" className="input" value={privateForm.end_time} onChange={(event) => setPrivateForm((prev) => ({ ...prev, end_time: event.target.value }))} />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">场馆</label>
                <select className="input" value={privateForm.location_id} onChange={(event) => setPrivateForm((prev) => ({ ...prev, location_id: event.target.value }))}>
                  <option value="">未指定</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>{location.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">场地</label>
                <select className="input" value={privateForm.court_id} onChange={(event) => setPrivateForm((prev) => ({ ...prev, court_id: event.target.value }))}>
                  <option value="">未指定</option>
                  {courts.map((court) => (
                    <option key={court.id} value={court.id}>{court.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">人数上限</label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  className="input"
                  value={privateForm.participant_limit}
                  onChange={(event) => setPrivateForm((prev) => ({ ...prev, participant_limit: Number(event.target.value) }))}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">备注</label>
                <textarea className="input min-h-[96px]" value={privateForm.notes} onChange={(event) => setPrivateForm((prev) => ({ ...prev, notes: event.target.value }))} />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-secondary" onClick={() => setPrivateModalOpen(false)}>
                取消
              </button>
              <button type="submit" className="btn-primary" disabled={savingPrivateLesson}>
                {savingPrivateLesson ? '保存中...' : '保存'}
              </button>
            </div>
          </form>
        </Modal>

        <Modal
          isOpen={programmeDetailOpen}
          onClose={() => {
            setProgrammeDetailOpen(false);
            setSelectedProgramme(null);
          }}
          title={selectedProgramme ? `训练营详情 · ${selectedProgramme.title}` : '训练营详情'}
          size="2xl"
        >
          {selectedProgramme && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-3 gap-3">
                <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                  <p className="text-xs text-gray-500">编号</p>
                  <p className="font-mono text-sm mt-1">{selectedProgramme.code}</p>
                </div>
                <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                  <p className="text-xs text-gray-500">日期</p>
                  <p className="text-sm mt-1">{selectedProgramme.start_date} ~ {selectedProgramme.end_date}</p>
                </div>
                <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                  <p className="text-xs text-gray-500">当前容量</p>
                  <p className="text-sm mt-1">{selectedProgramme.enrolled_count}/{selectedProgramme.max_participants} (最小 {selectedProgramme.min_participants})</p>
                </div>
              </div>

              <section className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <CalendarCheck2 className="h-5 w-5 text-primary-600" />
                  课次安排
                </h3>

                <form className="grid md:grid-cols-6 gap-2" onSubmit={addSession}>
                  <input type="date" className="input" value={sessionForm.session_date} onChange={(event) => setSessionForm((prev) => ({ ...prev, session_date: event.target.value }))} required />
                  <input type="time" className="input" value={sessionForm.start_time} onChange={(event) => setSessionForm((prev) => ({ ...prev, start_time: event.target.value }))} required />
                  <input type="time" className="input" value={sessionForm.end_time} onChange={(event) => setSessionForm((prev) => ({ ...prev, end_time: event.target.value }))} required />
                  <select className="input" value={sessionForm.court_id} onChange={(event) => setSessionForm((prev) => ({ ...prev, court_id: event.target.value }))}>
                    <option value="">场地</option>
                    {courts.map((court) => (
                      <option key={court.id} value={court.id}>{court.name}</option>
                    ))}
                  </select>
                  <input type="number" className="input" placeholder="容量" value={sessionForm.capacity} onChange={(event) => setSessionForm((prev) => ({ ...prev, capacity: event.target.value }))} />
                  <button className="btn-primary" type="submit">新增课次</button>
                </form>

                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {programmeDetail.sessions.length === 0 ? (
                    <p className="text-sm text-gray-500">还没有课次，先添加第一节课。</p>
                  ) : programmeDetail.sessions.map((session) => (
                    <div
                      key={session.id}
                      data-session-id={session.id}
                      className={clsx(
                        'p-3 rounded-lg border border-gray-200 bg-white flex items-center justify-between transition-colors',
                        isHighlighted('session_id', session.id) && 'border-primary-300 bg-primary-50 animate-pulse'
                      )}
                    >
                      <div>
                        <p className="font-medium">{session.session_date} {session.start_time.slice(0, 5)} - {session.end_time.slice(0, 5)}</p>
                        <p className="text-xs text-gray-500">场地ID: {session.court_id || '未指定'} · 容量: {session.capacity || '继承训练营'}</p>
                      </div>
                      <button
                        className="btn-secondary btn-sm"
                        onClick={async () => {
                          const result = await api.scheduling.deleteProgrammeSession(selectedProgramme.id, session.id);
                          if (result.error) {
                            toast.error(result.error);
                            return;
                          }
                          toast.success('课次已删除');
                          await loadProgrammeDetail(selectedProgramme.id);
                        }}
                      >
                        删除
                      </button>
                    </div>
                  ))}
                </div>
              </section>

              <section className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary-600" />
                  报名管理
                </h3>

                <form className="grid md:grid-cols-6 gap-2" onSubmit={addEnrollment}>
                  <input className="input md:col-span-2" placeholder="学员姓名" value={enrollmentForm.customer_name} onChange={(event) => setEnrollmentForm((prev) => ({ ...prev, customer_name: event.target.value }))} required disabled={enrollmentFormDisabled} />
                  <input className="input" placeholder="邮箱" type="email" value={enrollmentForm.customer_email} onChange={(event) => setEnrollmentForm((prev) => ({ ...prev, customer_email: event.target.value }))} disabled={enrollmentFormDisabled} />
                  <input className="input" placeholder="电话" value={enrollmentForm.customer_phone} onChange={(event) => setEnrollmentForm((prev) => ({ ...prev, customer_phone: event.target.value }))} disabled={enrollmentFormDisabled} />
                  <select className="input" value={enrollmentForm.status} onChange={(event) => setEnrollmentForm((prev) => ({ ...prev, status: event.target.value as 'active' | 'waitlisted' }))} disabled={enrollmentFormDisabled}>
                    <option value="active">active</option>
                    <option value="waitlisted">waitlisted</option>
                  </select>
                  <button className="btn-primary inline-flex items-center justify-center gap-1.5" type="submit" disabled={enrollmentFormDisabled}>
                    {savingEnrollment ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {savingEnrollment ? '录入中...' : '录入学员'}
                  </button>
                </form>

                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={enrollmentForm.auto_waitlist}
                    onChange={(event) => setEnrollmentForm((prev) => ({ ...prev, auto_waitlist: event.target.checked }))}
                    disabled={enrollmentFormDisabled}
                  />
                  人满后自动进入 waitlist
                </label>
                {isSelectedProgrammeEnrollmentBlocked ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    当前训练营状态为 <strong>{selectedProgramme.status}</strong>，禁止新增报名。若需继续报名，请先恢复训练营状态。
                  </div>
                ) : null}

                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {programmeDetail.enrollments.length === 0 ? (
                    <p className="text-sm text-gray-500">还没有报名记录。</p>
                  ) : programmeDetail.enrollments.map((enrollment) => (
                    <div
                      key={enrollment.id}
                      data-enrollment-id={enrollment.id}
                      className={clsx(
                        'p-3 rounded-lg border border-gray-200 bg-white flex items-center justify-between gap-3 transition-colors',
                        isHighlighted('enrollment_id', enrollment.id) && 'border-primary-300 bg-primary-50 animate-pulse'
                      )}
                    >
                      <div>
                        <p className="font-medium">{enrollment.customer_name}</p>
                        <p className="text-xs text-gray-500">{enrollment.customer_phone || enrollment.customer_email || '无联系方式'} · {enrollment.joined_at?.slice(0, 10)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={clsx('badge', STATUS_COLOR[enrollment.status] || 'badge-gray')}>{enrollment.status}</span>
                        {enrollment.status !== 'active' && (
                          <button className="btn-secondary btn-sm" onClick={() => changeEnrollmentStatus(enrollment, 'active')}>
                            设为 active
                          </button>
                        )}
                        {(enrollment.status === 'active' || enrollment.status === 'waitlisted') && (
                          <button className="btn-secondary btn-sm" onClick={() => changeEnrollmentStatus(enrollment, 'withdrawn')}>
                            退课
                          </button>
                        )}
                        {(enrollment.status === 'withdrawn' || enrollment.status === 'cancelled' || enrollment.status === 'completed') && (
                          <button className="btn-danger btn-sm" onClick={() => deleteEnrollment(enrollment)}>
                            删除
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}
        </Modal>

        <Modal
          isOpen={privateDetailOpen}
          onClose={() => {
            setPrivateDetailOpen(false);
            setSelectedPrivateLesson(null);
          }}
          title={selectedPrivateLesson ? `私教课详情 · ${selectedPrivateLesson.customer_name}` : '私教课详情'}
          size="xl"
        >
          {selectedPrivateLesson && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                  <p className="text-xs text-gray-500">时间</p>
                  <p className="text-sm mt-1">
                    {selectedPrivateLesson.date}
                    {selectedPrivateLesson.is_flexible_time
                      ? ' · 灵活时间'
                      : ` · ${selectedPrivateLesson.start_time?.slice(0, 5)} - ${selectedPrivateLesson.end_time?.slice(0, 5)}`}
                  </p>
                </div>
                <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                  <p className="text-xs text-gray-500">状态</p>
                  <p className="text-sm mt-1">
                    <span className={clsx('badge', STATUS_COLOR[selectedPrivateLesson.status] || 'badge-gray')}>
                      {selectedPrivateLesson.status}
                    </span>
                  </p>
                </div>
              </div>

              <section className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <UserRound className="h-5 w-5 text-primary-600" />
                  参与人管理 ({privateLessonDetail.participants.length}/{selectedPrivateLesson.participant_limit})
                </h3>

                <form className="grid md:grid-cols-4 gap-2" onSubmit={addPrivateParticipant}>
                  <input className="input" placeholder="姓名" value={participantForm.participant_name} onChange={(event) => setParticipantForm((prev) => ({ ...prev, participant_name: event.target.value }))} required />
                  <input className="input" placeholder="邮箱" type="email" value={participantForm.participant_email} onChange={(event) => setParticipantForm((prev) => ({ ...prev, participant_email: event.target.value }))} />
                  <input className="input" placeholder="电话" value={participantForm.participant_phone} onChange={(event) => setParticipantForm((prev) => ({ ...prev, participant_phone: event.target.value }))} />
                  <button className="btn-primary" type="submit">添加</button>
                </form>

                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {privateLessonDetail.participants.length === 0 ? (
                    <p className="text-sm text-gray-500">暂无参与人记录。</p>
                  ) : privateLessonDetail.participants.map((participant) => (
                    <div key={participant.id} className="p-3 rounded-lg border border-gray-200 bg-white flex items-center justify-between gap-3">
                      <div>
                        <p className="font-medium">
                          {participant.participant_name}
                          {participant.is_primary && <span className="text-xs text-gray-500 ml-1">(主联系人)</span>}
                        </p>
                        <p className="text-xs text-gray-500">{participant.participant_phone || participant.participant_email || '无联系方式'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={clsx('badge', STATUS_COLOR[participant.status] || 'badge-gray')}>{participant.status}</span>
                        {participant.status === 'active' ? (
                          <button className="btn-secondary btn-sm" onClick={() => updateParticipantStatus(participant, 'cancelled')}>
                            取消
                          </button>
                        ) : (
                          <button className="btn-secondary btn-sm" onClick={() => updateParticipantStatus(participant, 'active')}>
                            恢复
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}
        </Modal>
      </div>
    </ProtectedComponent>
  );
};

export default CourseScheduler;
